import type { EditorStarterAsset } from '../../assets/assets';
import type { EditorStarterItem } from '../../items/item-type';
import { AVATAR_SEGMENT_SOURCE_ROLE } from '../../items/video/video-item-type';
import type { TrackType } from '../../state/types';

export type PlaybackBoundaryAssetSourceKind =
  | 'blob'
  | 'variant'
  | 'original'
  | 'static'
  | 'none';

type ResolvePlaybackBoundaryAssetSourceKind = (
  asset: EditorStarterAsset,
  localUrl: string | undefined,
) => PlaybackBoundaryAssetSourceKind;

type TrackItemEntry = {
  item: EditorStarterItem;
  trackId: string;
};

type GetPlaybackBoundaryPremountFramesParams = {
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
  assets: Record<string, EditorStarterAsset>;
  localUrls: Record<string, string>;
  fps: number;
  resolveAssetSourceKind: ResolvePlaybackBoundaryAssetSourceKind;
};

const MEDIA_ITEM_TYPES = new Set(['audio', 'gif', 'image', 'video', 'webp']);
const BOUNDARY_PREMOUNT_SECONDS = 3;

export const getPlaybackItemDefaultPremountFrames = ({
  item,
  hasLocalUrl,
  prefetchEnabled,
  boundaryPremountFrames,
  fps,
  isPlayerPreview = false,
}: {
  item: EditorStarterItem;
  hasLocalUrl: boolean;
  prefetchEnabled: boolean;
  boundaryPremountFrames: number;
  fps: number;
  isPlayerPreview?: boolean;
}): number => {
  const premountFrames = BOUNDARY_PREMOUNT_SECONDS * fps;
  // Prepare the next remote scene in Player without granting a buffering
  // override. A fixed window also avoids a Sequence remount when caching ends.
  if (isPlayerPreview) return premountFrames;
  if (prefetchEnabled) return premountFrames;
  if (item.type !== 'video') return premountFrames;

  // sourceRole이 명시된 세그먼트는 incoming boundary override가 premount를
  // 담당한다. blob 완성 시 default 0→3초로 바꾸면 Remotion Sequence 구현이
  // 교체되어 재생 중 media element가 remount되므로 이 경로에서는 고정한다.
  if (
    item.hasAlpha === true &&
    item.sourceRole === AVATAR_SEGMENT_SOURCE_ROLE &&
    boundaryPremountFrames > 0
  ) {
    return 0;
  }
  return hasLocalUrl ? premountFrames : 0;
};

interface IsPlaybackBoundaryMediaPauseDisabledParams {
  currentFrame: number;
  itemFrom: number;
  premountFrames: number;
}

export interface PlaybackBoundaryWindow {
  itemFrom: number;
  premountFrames: number;
}

interface GetPlaybackBoundaryWindowsForItemsParams {
  items: EditorStarterItem[];
  boundaryPremountFramesByItemId?: ReadonlyMap<string, number>;
}

interface IsPlaybackBoundaryMediaPauseDisabledForWindowsParams {
  currentFrame: number;
  windows: PlaybackBoundaryWindow[];
}

interface GetPlaybackBoundaryPremountFramesForItemParams {
  itemId: string;
  defaultPremountFrames: number;
  boundaryPremountFramesByItemId?: ReadonlyMap<string, number>;
}

interface GetPlaybackBoundaryPremountFramesForSequenceStartParams {
  items: EditorStarterItem[];
  startFrame: number;
  defaultPremountFrames: number;
  boundaryPremountFramesByItemId?: ReadonlyMap<string, number>;
}

const isMediaItem = (item: EditorStarterItem): boolean =>
  MEDIA_ITEM_TYPES.has(item.type);

const hasAssetId = (
  item: EditorStarterItem,
): item is EditorStarterItem & { assetId: string } =>
  'assetId' in item && typeof item.assetId === 'string';

const isPremountableSourceKind = (
  kind: PlaybackBoundaryAssetSourceKind,
): boolean => kind === 'blob' || kind === 'variant' || kind === 'static';

// Keep pause override limited to the boundary preparation window. Disabling
// pauseWhenBuffering globally hides real buffering and was only a fallback.
export const isPlaybackBoundaryMediaPauseDisabled = ({
  currentFrame,
  itemFrom,
  premountFrames,
}: IsPlaybackBoundaryMediaPauseDisabledParams): boolean => {
  if (premountFrames <= 0) return false;

  return (
    currentFrame >= itemFrom - premountFrames &&
    currentFrame < itemFrom + premountFrames
  );
};

export const getPlaybackBoundaryWindowsForItems = ({
  items,
  boundaryPremountFramesByItemId,
}: GetPlaybackBoundaryWindowsForItemsParams): PlaybackBoundaryWindow[] =>
  items.flatMap((item) => {
    const premountFrames = boundaryPremountFramesByItemId?.get(item.id) ?? 0;
    if (premountFrames <= 0) return [];

    return [
      {
        itemFrom: item.from,
        premountFrames,
      },
    ];
  });

export const isPlaybackBoundaryMediaPauseDisabledForWindows = ({
  currentFrame,
  windows,
}: IsPlaybackBoundaryMediaPauseDisabledForWindowsParams): boolean =>
  windows.some((window) =>
    isPlaybackBoundaryMediaPauseDisabled({
      currentFrame,
      itemFrom: window.itemFrom,
      premountFrames: window.premountFrames,
    }),
  );

export const getPlaybackBoundaryPremountFramesForItem = ({
  itemId,
  defaultPremountFrames,
  boundaryPremountFramesByItemId,
}: GetPlaybackBoundaryPremountFramesForItemParams): number =>
  Math.max(
    defaultPremountFrames,
    boundaryPremountFramesByItemId?.get(itemId) ?? 0,
  );

export const getPlaybackBoundaryPremountFramesForSequenceStart = ({
  items,
  startFrame,
  defaultPremountFrames,
  boundaryPremountFramesByItemId,
}: GetPlaybackBoundaryPremountFramesForSequenceStartParams): number => {
  // Outer Sequences should only open early when the sequence itself starts at
  // a boundary item. Later boundary items must stay item-scoped so remote
  // originals in the same group do not get premounted by accident.
  return Math.max(
    defaultPremountFrames,
    ...items
      .filter((item) => item.from === startFrame)
      .map((item) => boundaryPremountFramesByItemId?.get(item.id) ?? 0),
  );
};

const getItemEndFrame = (item: EditorStarterItem): number =>
  item.from + Math.abs(item.durationInFrames);

const collectRenderedMediaEntries = (
  tracks: TrackType[],
  items: Record<string, EditorStarterItem>,
): TrackItemEntry[] => {
  return tracks.flatMap((track) =>
    track.items
      .map((itemId) => items[itemId])
      .filter((item): item is EditorStarterItem => {
        if (!item) return false;
        return isMediaItem(item);
      })
      .map((item) => ({
        item,
        trackId: track.id,
      })),
  );
};

type AdjacentAvatarSegmentBoundary = {
  outgoing: Extract<EditorStarterItem, { type: 'video' }>;
  incoming: Extract<EditorStarterItem, { type: 'video' }>;
};

const isAlphaAvatarSegment = (
  item: EditorStarterItem,
  assets: Record<string, EditorStarterAsset>,
): item is Extract<EditorStarterItem, { type: 'video' }> => {
  if (
    item.type !== 'video' ||
    item.hasAlpha !== true ||
    item.sourceRole !== AVATAR_SEGMENT_SOURCE_ROLE ||
    !hasAssetId(item)
  ) {
    return false;
  }

  const asset = assets[item.assetId];
  return asset?.type === 'video' && asset.hasAlpha === true;
};

const getAdjacentAvatarSegmentBoundaries = ({
  tracks,
  items,
  assets,
}: Pick<
  GetPlaybackBoundaryPremountFramesParams,
  'tracks' | 'items' | 'assets'
>): AdjacentAvatarSegmentBoundary[] =>
  tracks.flatMap((track) => {
    const trackItems = track.items
      .map((itemId) => items[itemId])
      .filter((item): item is EditorStarterItem => Boolean(item))
      .slice()
      .sort((a, b) => a.from - b.from);

    const boundaries: AdjacentAvatarSegmentBoundary[] = [];
    for (let index = 1; index < trackItems.length; index++) {
      const outgoing = trackItems[index - 1];
      const incoming = trackItems[index];
      if (!outgoing || !incoming) continue;
      if (getItemEndFrame(outgoing) !== incoming.from) continue;
      if (
        !isAlphaAvatarSegment(outgoing, assets) ||
        !isAlphaAvatarSegment(incoming, assets)
      ) {
        continue;
      }
      boundaries.push({ outgoing, incoming });
    }
    return boundaries;
  });

export const getPlaybackBoundaryHoldIncomingItemIdByOutgoingItemId = (
  params: GetPlaybackBoundaryPremountFramesParams,
): Map<string, string> =>
  new Map(
    getAdjacentAvatarSegmentBoundaries(params).map(({ outgoing, incoming }) => [
      outgoing.id,
      incoming.id,
    ]),
  );

export const getPlaybackBoundaryPremountFramesByItemId = ({
  tracks,
  items,
  assets,
  localUrls,
  fps,
  resolveAssetSourceKind,
}: GetPlaybackBoundaryPremountFramesParams): Map<string, number> => {
  const mediaEntries = collectRenderedMediaEntries(tracks, items);
  const entriesByEndFrame = new Map<number, TrackItemEntry[]>();

  for (const entry of mediaEntries) {
    if (entry.item.durationInFrames === 0) continue;

    const endFrame = getItemEndFrame(entry.item);
    const entries = entriesByEndFrame.get(endFrame) ?? [];
    entries.push(entry);
    entriesByEndFrame.set(endFrame, entries);
  }

  const premountFrames = Math.floor(BOUNDARY_PREMOUNT_SECONDS * fps);
  const premountFramesByItemId = new Map<string, number>();

  // defer-baking 아바타는 짧은 투명 WebM 여러 개가 같은 트랙에 정확히
  // 맞닿는다. 이 명시적 source role에만 original remote도 일찍 마운트한다.
  // generic remote 영상에는 적용하지 않아 기존 CDN 보호 정책을 유지한다.
  for (const { incoming } of getAdjacentAvatarSegmentBoundaries({
    tracks,
    items,
    assets,
  })) {
    premountFramesByItemId.set(incoming.id, premountFrames);
  }

  // The stall happens when playback crosses from a media item on one track
  // into a media item on another track at the same frame. Generic same-track
  // adjacency remains outside this policy; only the explicit avatar case above
  // is covered.
  for (const entry of mediaEntries) {
    const { item, trackId } = entry;
    if (item.from <= 0 || !hasAssetId(item)) continue;

    const boundarySources = entriesByEndFrame.get(item.from) ?? [];
    const hasCrossTrackBoundarySource = boundarySources.some(
      (source) => source.trackId !== trackId && source.item.id !== item.id,
    );
    if (!hasCrossTrackBoundarySource) continue;

    const asset = assets[item.assetId];
    if (!asset) continue;

    // Remote originals keep the existing no-premount policy because early
    // mounting them can reintroduce the 503-prone buffering path.
    const sourceKind = resolveAssetSourceKind(asset, localUrls[asset.id]);
    if (!isPremountableSourceKind(sourceKind)) continue;

    premountFramesByItemId.set(item.id, premountFrames);
  }

  return premountFramesByItemId;
};
